import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { Users, TrendingUp, Award, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const SupervisorTeam = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsRes, usersRes, coursesRes] = await Promise.all([
        axiosInstance.get('/admin/analytics'),
        axiosInstance.get('/admin/users'),
        axiosInstance.get('/courses')
      ]);
      
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.filter(u => u.role === 'worker'));
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t('supervisorPages.failedToLoadTeam'));
    } finally {
      setLoading(false);
    }
  };

  const getCompletionRate = (workerUser) => {
    if (!workerUser.completedCourses || courses.length === 0) return 0;
    return Math.round((workerUser.completedCourses.length / courses.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button variant="ghost" onClick={() => window.history.back()} className="mb-4 hover:bg-gray-100">
            {t('common.back')}
          </Button>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => window.history.back()} className="mb-4 hover:bg-gray-100" data-testid="back-button">
          {t('common.back')}
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('supervisorPages.teamProgress')}</h1>
          <p className="text-gray-600 mt-1">{t('supervisorPages.teamProgressDesc')}</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                {t('supervisorPages.totalWorkers')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{users.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Award className="w-4 h-4 text-green-600" />
                {t('supervisorPages.fullyCompliant')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {users.filter(u => getCompletionRate(u) === 100).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-orange-600" />
                {t('supervisorPages.inProgress')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {users.filter(u => getCompletionRate(u) > 0 && getCompletionRate(u) < 100).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-red-600" />
                {t('supervisorPages.notStarted')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">
                {users.filter(u => getCompletionRate(u) === 0).length}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('supervisorPages.workerTrainingStatus')}</CardTitle>
            <CardDescription>{t('supervisorPages.individualProgress')}</CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('supervisorPages.noTeamMembers')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((worker) => {
                  const completionRate = getCompletionRate(worker);
                  return (
                    <div key={worker.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{worker.name}</h4>
                          <p className="text-sm text-gray-500">{worker.email}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">{completionRate}%</div>
                          <p className="text-xs text-gray-500">
                            {worker.completedCourses?.length || 0} {t('supervisorPages.coursesOf')} {courses.length}
                          </p>
                        </div>
                      </div>
                      <Progress value={completionRate} className="h-2" />
                      <div className="mt-3 flex flex-wrap gap-2">
                        {completionRate === 100 ? (
                          <Badge className="bg-green-100 text-green-800">{t('supervisorPages.fullyCompliant')}</Badge>
                        ) : completionRate > 0 ? (
                          <Badge className="bg-orange-100 text-orange-800">{t('supervisorPages.inProgress')}</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">{t('supervisorPages.notStarted')}</Badge>
                        )}
                      </div>
                      {worker.completedCourses && worker.completedCourses.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-medium text-gray-500 mb-2">{t('supervisorPages.completedCoursesLabel')}:</p>
                          <div className="flex flex-wrap gap-1">
                            {worker.completedCourses.map((course, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                &#10003; {course.courseName || course.courseId}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {courses.length > 0 && worker.completedCourses && (
                        <div className="mt-2">
                          {courses.filter(c => !worker.completedCourses.some(cc => cc.courseId === c.id)).length > 0 && (
                            <>
                              <p className="text-xs font-medium text-gray-500 mb-2">{t('supervisorPages.remainingCourses')}:</p>
                              <div className="flex flex-wrap gap-1">
                                {courses.filter(c => !worker.completedCourses.some(cc => cc.courseId === c.id)).map((course, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-200">
                                    &#9675; {course.title}
                                  </Badge>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SupervisorTeam;
